import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../utils/api';
import {
  RackReservation,
  EquipmentSlotItem,
  SpaceMetrics,
  calculateSpaceMetrics,
  buildReservationMap,
  addReservation as addResLogic,
  removeReservation as removeResLogic,
} from '../utils/rackReservation.logic';

const STORAGE_KEY_PREFIX = 'dc_rack_reservations_';

export function useRackReservations(
  rackId: string | number | undefined,
  totalU: number,
  equipmentList: EquipmentSlotItem[]
) {
  const storageKey = rackId ? `${STORAGE_KEY_PREFIX}${rackId}` : null;
  const currentKeyRef = useRef(storageKey);

  const [reservations, setReservations] = useState<RackReservation[]>(() => {
    if (!storageKey || typeof window === 'undefined') return [];
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });

  // When storageKey changes, load new rack's reservations and fetch from backend
  useEffect(() => {
    currentKeyRef.current = storageKey;
    if (!storageKey || typeof window === 'undefined') {
      setReservations([]);
      return;
    }
    try {
      const saved = localStorage.getItem(storageKey);
      setReservations(saved ? JSON.parse(saved) : []);
    } catch {
      setReservations([]);
    }

    if (rackId) {
      api.get(`/api/racks/${rackId}/reservations`)
        .then(res => {
          if (Array.isArray(res.data) && currentKeyRef.current === storageKey) {
            const mapped: RackReservation[] = res.data.map((item: any) => ({
              id: String(item.id),
              rack_id: item.rack_id,
              u_start: item.u_start,
              u_height: item.u_height,
              tenant: item.tenant || '',
              description: item.description || '',
              face: item.face || 'both',
              created_by: item.created_by,
              created_at: item.created_at,
            }));
            setReservations(mapped);
            try {
              localStorage.setItem(storageKey, JSON.stringify(mapped));
            } catch {}
          }
        })
        .catch(() => {
          // Gracefully keep cached localStorage reservations if offline or 404
        });
    }
  }, [storageKey, rackId]);

  // Save only when currentKeyRef matches storageKey to prevent cross-rack data leakage
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    if (currentKeyRef.current !== storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(reservations));
    } catch {}
  }, [storageKey, reservations]);

  const reservationMap = useMemo(() => buildReservationMap(reservations), [reservations]);

  const spaceMetrics: SpaceMetrics = useMemo(() => {
    return calculateSpaceMetrics(totalU, equipmentList, reservations);
  }, [totalU, equipmentList, reservations]);

  const add = useCallback(async (newRes: Omit<RackReservation, 'id'>) => {
    const tempId = `res-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const fullRes: RackReservation = { ...newRes, id: tempId };
    const result = addResLogic(reservations, fullRes, equipmentList, totalU);
    if (result.error) {
      return { success: false, error: result.error };
    }
    setReservations(result.reservations);

    if (rackId) {
      try {
        const response = await api.post(`/api/racks/${rackId}/reservations`, {
          u_start: newRes.u_start,
          u_height: newRes.u_height,
          tenant: newRes.tenant,
          description: newRes.description,
          face: newRes.face || 'both',
        });
        if (response.data && response.data.id) {
          const backendId = String(response.data.id);
          setReservations(prev =>
            prev.map(r => (r.id === tempId ? { ...r, id: backendId } : r))
          );
        }
      } catch {
        // Retain optimistic local state if network call fails
      }
    }

    return { success: true };
  }, [reservations, equipmentList, totalU, rackId]);

  const remove = useCallback(async (id: string) => {
    setReservations(prev => removeResLogic(prev, id));
    if (rackId) {
      try {
        await api.delete(`/api/racks/${rackId}/reservations/${id}`);
      } catch {
        // Silently ignore if already deleted or local-only
      }
    }
  }, [rackId]);

  return {
    reservations,
    reservationMap,
    spaceMetrics,
    addReservation: add,
    removeReservation: remove,
  };
}
